"use client";

import { motion } from "motion/react";
import { Sparkles } from "lucide-react";
import Link from "next/link";
import { useTranslation } from "react-i18next";

// * ─── Hero Section ───────────────────────────────────────────────────────────
export function HomeHeroSection() {
  const { t } = useTranslation("common");

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden">
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute top-1/4 right-1/4 w-96 h-96 bg-walnut-400/20 dark:bg-walnut-600/20 rounded-full blur-3xl"
      />

      <div className="max-w-7xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 backdrop-blur-[5px] bg-white/10 dark:bg-white/5 px-6 py-3 rounded-[33px] border border-white/20 dark:border-white/10 mb-8 glass-shadow-outer relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
            />
            <Sparkles className="w-5 h-5 text-walnut-600 dark:text-walnut-400" />
            <span className="text-sm font-medium text-grey-800 dark:text-grey-200 relative z-10">
              {t("hero.badge")}
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 text-grey-900 dark:text-white">
            {t("hero.title")}
            <br />
            <span className="bg-gradient-to-r from-walnut-600 to-walnut-800 dark:from-walnut-400 dark:to-walnut-600 bg-clip-text text-transparent">
              {t("hero.titleHighlight")}
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-grey-600 dark:text-grey-300 mb-8 max-w-3xl mx-auto">
            {t("hero.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/quote"
              className="relative px-8 py-4 rounded-[33px] bg-gradient-to-r from-walnut-600 to-walnut-700 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105 inline-flex items-center justify-center gap-2"
            >
              {t("hero.launchStudio")}
              <Sparkles className="w-5 h-5" />
            </Link>
            <div className="relative inline-flex">
              <Link
                href="/diecuts"
                className="relative px-8 py-4 rounded-[33px] backdrop-blur-[5px] bg-white/10 border border-white/20 text-grey-800 dark:text-white font-semibold hover:bg-white/20 transition-all duration-300 hover:scale-105 glass-shadow-outer inline-flex items-center justify-center gap-2"
              >
                {t("hero.exploreTemplates")}
                <div className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
